import { BrushCleaning } from 'lucide-react';
import { STAFF_NEEDS, staffOf, type StaffContent, type StaffRole } from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, NumberField, idFor, type NumberSpec } from './form';

type StaffNumbers = Exclude<keyof StaffContent, 'visible' | 'roles'>;
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const PAYDAY: NumberSpec<StaffNumbers>[] = [
  { key: 'payHour', label: 'Payday hour (London, 0 to 23)', unit: 'number', min: 0, max: 23, whole: true },
];
const EFFECTS: NumberSpec<StaffNumbers>[] = [
  {
    key: 'cleanerHygiene',
    label: 'Cleaner: hygiene after a sleep at home is at least',
    unit: 'number',
    min: 0,
    max: 100,
    whole: true,
  },
  {
    key: 'chefHungerBonus',
    label: 'Chef: cooking at home fills this much more',
    unit: 'number',
    min: 0,
    max: 100,
    whole: true,
  },
  {
    key: 'guardEnergyBonus',
    label: 'Guard: a sleep at home restores this much more',
    unit: 'number',
    min: 0,
    max: 100,
    whole: true,
  },
];
const WAGE: NumberSpec<'wagePenceWeekly'> = {
  key: 'wagePenceWeekly',
  label: 'Wage a week',
  unit: 'pounds',
  min: 1,
  max: 100_000_000,
};
const TIER: NumberSpec<'minTier'> = {
  key: 'minTier',
  label: 'Smallest home tier (1 to 4)',
  unit: 'number',
  min: 1,
  max: 4,
  whole: true,
};
const NEED_LABELS: Record<StaffRole['needs'], string> = {
  nothing: 'Nothing',
  home: 'A home of at least the tier below',
  car: 'A car (Cars, coming)',
  baby: 'A baby (Family, coming)',
};

// Staff (as Lagos Life's Staff): the roles for hire, their wages, what they need and what they do; payday. Part of
// the content draft, published with it. The cleaner, chef and guard ids do the work in the game; renaming them
// is fine, changing their ids is not.
export function StaffPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const staff = staffOf(working ?? {});
  const change = (fn: (draft: StaffContent) => void) =>
    editor.edit((content) => {
      content.staff ??= structuredClone(staffOf(content));
      fn(content.staff);
    });
  const role = (id: string, fn: (entry: StaffRole) => void) =>
    change((draft) => {
      const entry = draft.roles.find((item) => item.id === id);
      if (entry) fn(entry);
    });
  const numbers = (specs: NumberSpec<StaffNumbers>[]) =>
    specs.map((spec) => (
      <NumberField
        key={spec.key}
        id={`staff-${spec.key}`}
        spec={spec}
        value={staff[spec.key]}
        onChange={(value) => change((draft) => void (draft[spec.key] = value))}
      />
    ));
  return (
    <section>
      <PageHeader
        title="Staff"
        description="Help for the house (as Lagos Life's Staff): who is for hire, their wages, what they need and what they do. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Payday">
            <h2 className="content-subtitle">
              <BrushCleaning size={16} aria-hidden="true" /> Payday
            </h2>
            <label className="designer-check">
              <Switch
                checked={staff.visible}
                aria-label="Staff open"
                onCheckedChange={(on) => change((draft) => void (draft.visible = on))}
              />{' '}
              Players can hire staff
            </label>
            <div className="content-grid">
              <div className="admin-field">
                <Label htmlFor="staff-payWeekday">Payday (weekday)</Label>
                <select
                  id="staff-payWeekday"
                  className="content-select"
                  value={staff.payWeekday}
                  onChange={(e) => change((draft) => void (draft.payWeekday = Number(e.target.value)))}
                >
                  {WEEKDAYS.map((day, index) => (
                    <option key={day} value={index}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
              {numbers(PAYDAY)}
            </div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="What staff do">
            <h2 className="content-subtitle">What staff do</h2>
            <div className="content-grid">{numbers(EFFECTS)}</div>
          </fieldset>
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Roles">
            <h2 className="content-subtitle">Roles</h2>
            {staff.roles.map((item) => (
              <div className="content-grid content-row" key={item.id} data-testid={`staff-${item.id}`}>
                <Field id={`staff-${item.id}-label`} label="Role">
                  <Input
                    id={`staff-${item.id}-label`}
                    value={item.label}
                    maxLength={40}
                    onChange={(e) => role(item.id, (entry) => void (entry.label = e.target.value))}
                  />
                </Field>
                <Field id={`staff-${item.id}-name`} label="Name">
                  <Input
                    id={`staff-${item.id}-name`}
                    value={item.name}
                    maxLength={40}
                    onChange={(e) => role(item.id, (entry) => void (entry.name = e.target.value))}
                  />
                </Field>
                <Field id={`staff-${item.id}-emoji`} label="Emoji">
                  <Input
                    id={`staff-${item.id}-emoji`}
                    value={item.emoji}
                    maxLength={16}
                    onChange={(e) => role(item.id, (entry) => void (entry.emoji = e.target.value))}
                  />
                </Field>
                <NumberField
                  id={`staff-${item.id}-wage`}
                  spec={WAGE}
                  value={item.wagePenceWeekly}
                  onChange={(value) => role(item.id, (entry) => void (entry.wagePenceWeekly = value))}
                />
                <Field id={`staff-${item.id}-blurb`} label="What they are like">
                  <Input
                    id={`staff-${item.id}-blurb`}
                    value={item.blurb}
                    maxLength={200}
                    onChange={(e) => role(item.id, (entry) => void (entry.blurb = e.target.value))}
                  />
                </Field>
                <Field id={`staff-${item.id}-perk`} label="What they do for you">
                  <Input
                    id={`staff-${item.id}-perk`}
                    value={item.perk}
                    maxLength={200}
                    onChange={(e) => role(item.id, (entry) => void (entry.perk = e.target.value))}
                  />
                </Field>
                <div className="admin-field">
                  <Label htmlFor={`staff-${item.id}-needs`}>Needs</Label>
                  <select
                    id={`staff-${item.id}-needs`}
                    className="content-select"
                    value={item.needs}
                    onChange={(e) =>
                      role(item.id, (entry) => void (entry.needs = e.target.value as StaffRole['needs']))
                    }
                  >
                    {STAFF_NEEDS.map((need) => (
                      <option key={need} value={need}>
                        {NEED_LABELS[need]}
                      </option>
                    ))}
                  </select>
                </div>
                <NumberField
                  id={`staff-${item.id}-tier`}
                  spec={TIER}
                  value={item.minTier}
                  onChange={(value) => role(item.id, (entry) => void (entry.minTier = value))}
                />
                <label className="designer-check">
                  <Switch
                    checked={item.livesIn}
                    aria-label={`${item.label} lives in`}
                    onCheckedChange={(on) => role(item.id, (entry) => void (entry.livesIn = on))}
                  />{' '}
                  Lives in
                </label>
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`${item.label} for hire`}
                    onCheckedChange={(on) => role(item.id, (entry) => void (entry.visible = on))}
                  />{' '}
                  For hire (players who have them keep them)
                </label>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                change((draft) =>
                  draft.roles.push({
                    id: idFor('New role', new Set(draft.roles.map((entry) => entry.id)), 'role', 30),
                    label: 'New role',
                    name: 'Someone',
                    emoji: '🧑🏾‍🔧',
                    blurb: 'What they are like, in a line or two.',
                    perk: 'What they do for you, in a line.',
                    wagePenceWeekly: 20_000,
                    needs: 'nothing',
                    minTier: 1,
                    livesIn: false,
                    visible: false,
                  }),
                )
              }
            >
              Add a role
            </Button>
          </fieldset>
        </div>
      )}
    </section>
  );
}
